<template>
  <v-card class="mb-4">
    <v-card-title class="d-flex justify-space-between align-center">
      <span>基本情報</span>
      <v-btn
        v-if="!isEditing"
        small
        color="primary"
        @click="startEdit"
      >
        <v-icon
          small
          left
        >
          {{ icons.mdiPencil }}
        </v-icon>
        編集
      </v-btn>
    </v-card-title>

    <v-card-text>
      <!-- 表示モード -->
      <template v-if="!isEditing">
        <v-row>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <div class="text-subtitle-2 text--secondary">
              会員番号
            </div>
            <div>{{ customerMemberNumber }}</div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <div class="text-subtitle-2 text--secondary">
              名前
            </div>
            <div>{{ customerName }}</div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <div class="text-subtitle-2 text--secondary">
              誕生日
            </div>
            <div>{{ customerBirthday }}</div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <div class="text-subtitle-2 text--secondary">
              領収書宛名
            </div>
            <div class="d-flex align-center">
              <span>{{ customerReceiptAddressee }}</span>
              <v-btn
                v-if="hasReceiptAddressee"
                icon
                x-small
                class="ml-1"
                @click="copyReceiptAddressee"
              >
                <v-icon x-small>
                  {{ icons.mdiContentCopy }}
                </v-icon>
              </v-btn>
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <div class="text-subtitle-2 text--secondary">
              来店回数
            </div>
            <div>{{ customerVisitCount }}回</div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <div class="text-subtitle-2 text--secondary">
              最終来店日時
            </div>
            <div>{{ customerLastVisitedAt }}</div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <div class="text-subtitle-2 text--secondary">
              作成日時
            </div>
            <div>{{ customerCreatedAt }}</div>
          </v-col>
          <!-- カスタムフィールド表示 -->
          <v-col
            v-for="field in customFieldsWithValues"
            :key="field.id"
            cols="12"
            sm="6"
            md="4"
          >
            <div class="text-subtitle-2 text--secondary">
              {{ field.name }}
            </div>
            <div>{{ formatCustomFieldValue(field) }}</div>
          </v-col>
        </v-row>
      </template>

      <!-- 編集モード -->
      <template v-else>
        <v-form
          ref="form"
          v-model="formValid"
        >
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="formData.name"
                label="名前"
                :rules="[v => !!v || '名前は必須です']"
                required
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-menu
                transition="scale-transition"
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    :value="birthdayDisplay"
                    label="誕生日"
                    readonly
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="clearBirthday"
                  />
                </template>
                <v-date-picker
                  :value="birthdayPickerValue"
                  locale="ja"
                  :day-format="date => new Date(date).getDate()"
                  no-title
                  @input="onBirthdaySelect"
                />
              </v-menu>
              <v-checkbox
                v-model="includeYear"
                label="年を登録する"
                dense
                hide-details
                class="mt-0"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="formData.receiptAddressee"
                label="領収書宛名"
              />
            </v-col>
            <!-- カスタムフィールド編集 -->
            <v-col
              v-for="field in customFields"
              :key="field.id"
              cols="12"
              sm="6"
            >
              <!-- number型 -->
              <v-text-field
                v-if="field.attributes.fieldType === 'number'"
                v-model="formData.customFieldValues[field.id]"
                :label="field.attributes.name"
                type="number"
              />
              <!-- text型 -->
              <v-text-field
                v-else-if="field.attributes.fieldType === 'text'"
                v-model="formData.customFieldValues[field.id]"
                :label="field.attributes.name"
              />
              <!-- boolean型 -->
              <v-checkbox
                v-else-if="field.attributes.fieldType === 'boolean'"
                v-model="formData.customFieldValues[field.id]"
                :label="field.attributes.name"
                :true-value="'true'"
                :false-value="'false'"
              />
            </v-col>
          </v-row>
        </v-form>

        <div class="d-flex justify-end mt-4">
          <v-btn
            text
            class="mr-2"
            @click="cancelEdit"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="error"
            text
            class="mr-2"
            @click="confirmDelete"
          >
            削除
          </v-btn>
          <v-btn
            color="primary"
            :loading="isSaving"
            :disabled="!formValid || isSaving"
            @click="saveEdit"
          >
            保存
          </v-btn>
        </div>
      </template>
    </v-card-text>

    <!-- 削除確認ダイアログ -->
    <v-dialog
      v-model="deleteDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title>顧客を削除</v-card-title>
        <v-card-text>
          「{{ customerName }}」を削除してもよろしいですか？
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="deleteDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="error"
            :loading="isDeleting"
            @click="executeDelete"
          >
            削除
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {
  ref, computed, watch, getCurrentInstance, onMounted,
} from '@vue/composition-api'
import { mdiPencil, mdiContentCopy } from '@mdi/js'
import { format } from 'date-fns'
import CustomerCustomFieldApi from '@/api/waiter/CustomerCustomField'

export default {
  name: 'CustomerBasicInfo',
  props: {
    customer: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const form = ref(null)
    const formValid = ref(false)
    const isEditing = ref(false)
    const isSaving = ref(false)
    const isDeleting = ref(false)
    const deleteDialog = ref(false)
    const customFields = ref([])
    const formData = ref({
      name: '',
      birthday: null,
      receiptAddressee: '',
      customFieldValues: {},
    })
    const includeYear = ref(true)

    // カスタムフィールド一覧を取得
    const fetchCustomFields = async () => {
      try {
        const res = await CustomerCustomFieldApi.getCustomerCustomFields()
        if (res.data && res.data.customerCustomFields && res.data.customerCustomFields.data) {
          customFields.value = res.data.customerCustomFields.data
        }
      } catch (e) {
        console.error('Failed to fetch custom fields:', e)
      }
    }

    onMounted(() => {
      fetchCustomFields()
    })

    const formatDateTime = dateString => {
      if (!dateString) return '-'
      try {
        return format(new Date(dateString), 'yyyy/MM/dd HH:mm')
      } catch (e) {
        return dateString
      }
    }

    const customerMemberNumber = computed(() => {
      return (props.customer.attributes && props.customer.attributes.memberNumber) || '-'
    })

    const customerName = computed(() => {
      return (props.customer.attributes && props.customer.attributes.name) || '-'
    })

    const customerBirthday = computed(() => {
      const value = props.customer.attributes && props.customer.attributes.birthday
      if (!value) return '-'
      try {
        const date = new Date(value)
        // 年が1900の場合は月日のみ表示
        if (date.getFullYear() === 1900) {
          return format(date, 'MM/dd')
        }

        return format(date, 'yyyy/MM/dd')
      } catch (e) {
        return value
      }
    })

    // 編集フォーム用の誕生日表示
    const birthdayDisplay = computed(() => {
      if (!formData.value.birthday) return ''
      const date = new Date(formData.value.birthday)
      if (includeYear.value) {
        return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`
      }

      return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`
    })

    // date-picker用の値
    const birthdayPickerValue = computed(() => {
      if (!formData.value.birthday) return null
      const date = new Date(formData.value.birthday)
      if (date.getFullYear() === 1900) {
        const currentYear = new Date().getFullYear()

        return `${currentYear}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      }

      return formData.value.birthday
    })

    const onBirthdaySelect = dateString => {
      if (!dateString) {
        formData.value.birthday = null

        return
      }
      if (includeYear.value) {
        formData.value.birthday = dateString
      } else {
        const parts = dateString.split('-')
        formData.value.birthday = `1900-${parts[1]}-${parts[2]}`
      }
    }

    const clearBirthday = () => {
      formData.value.birthday = null
    }

    const customerReceiptAddressee = computed(() => {
      return (props.customer.attributes && props.customer.attributes.receiptAddressee) || '-'
    })

    const hasReceiptAddressee = computed(() => {
      return !!(props.customer.attributes && props.customer.attributes.receiptAddressee)
    })

    const copyReceiptAddressee = async () => {
      const addressee = props.customer.attributes && props.customer.attributes.receiptAddressee
      if (!addressee) return

      try {
        await navigator.clipboard.writeText(addressee)
        vm.$toast.success('領収書宛名をコピーしました')
      } catch (e) {
        vm.$toast.error('コピーに失敗しました')
      }
    }

    const customerVisitCount = computed(() => {
      return (props.customer.attributes && props.customer.attributes.visitCount) || 0
    })

    const customerLastVisitedAt = computed(() => {
      const value = props.customer.attributes && props.customer.attributes.lastVisitedAt

      return formatDateTime(value)
    })

    const customerCreatedAt = computed(() => {
      const value = props.customer.attributes && props.customer.attributes.createdAt

      return formatDateTime(value)
    })

    // カスタムフィールドの値を取得
    const getCustomFieldValues = () => {
      const customFieldValues = props.customer.attributes && props.customer.attributes.customFieldValues
      if (!customFieldValues || !customFieldValues.data) return []

      return customFieldValues.data
    }

    // カスタムフィールドと値を結合
    const customFieldsWithValues = computed(() => {
      const values = getCustomFieldValues()

      return customFields.value.map(field => {
        const valueObj = values.find(
          v => v.attributes && Number(v.attributes.customerCustomFieldId) === Number(field.id),
        )

        return {
          id: field.id,
          name: field.attributes.name,
          fieldType: field.attributes.fieldType,
          value: valueObj ? valueObj.attributes.value : null,
          typedValue: valueObj ? valueObj.attributes.typedValue : null,
          valueId: valueObj ? valueObj.id : null,
        }
      })
    })

    // カスタムフィールド値のフォーマット
    const formatCustomFieldValue = field => {
      if (field.value === null || field.value === undefined || field.value === '') return '-'

      if (field.fieldType === 'boolean') {
        return field.typedValue === true ? 'はい' : 'いいえ'
      }

      return field.value
    }

    const initializeForm = () => {
      const birthday = (props.customer.attributes && props.customer.attributes.birthday) || null

      // カスタムフィールド値を初期化
      const customFieldValuesMap = {}
      customFieldsWithValues.value.forEach(field => {
        customFieldValuesMap[field.id] = field.value || ''
      })

      formData.value = {
        name: (props.customer.attributes && props.customer.attributes.name) || '',
        birthday,
        receiptAddressee: (props.customer.attributes && props.customer.attributes.receiptAddressee) || '',
        customFieldValues: customFieldValuesMap,
      }
      // 年が1900の場合は年なし設定
      if (birthday) {
        const year = new Date(birthday).getFullYear()
        includeYear.value = year !== 1900
      } else {
        includeYear.value = true
      }
    }

    const startEdit = () => {
      initializeForm()
      isEditing.value = true
    }

    const cancelEdit = () => {
      isEditing.value = false
      initializeForm()
    }

    const saveEdit = async () => {
      if (!await form.value.validate()) return

      isSaving.value = true

      // カスタムフィールド値を属性形式に変換
      const customFieldValuesAttributes = []
      customFieldsWithValues.value.forEach(field => {
        const newValue = formData.value.customFieldValues[field.id]
        // 値が空でない場合、または既存の値がある場合に追加
        if (newValue !== '' && newValue !== null && newValue !== undefined) {
          const attr = {
            customer_custom_field_id: Number(field.id),
            value: String(newValue),
          }
          // 既存の値がある場合はIDを含める（更新）
          if (field.valueId) {
            attr.id = Number(field.valueId)
          }
          customFieldValuesAttributes.push(attr)
        } else if (field.valueId) {
          // 既存の値があり、新しい値が空の場合は削除
          customFieldValuesAttributes.push({
            id: Number(field.valueId),
            _destroy: true,
          })
        }
      })

      emit('update', {
        id: props.customer.id,
        name: formData.value.name,
        birthday: formData.value.birthday,
        receiptAddressee: formData.value.receiptAddressee,
        customer_custom_field_values_attributes: customFieldValuesAttributes,
        callback: success => {
          isSaving.value = false
          if (success) {
            isEditing.value = false
          }
        },
      })
    }

    const confirmDelete = () => {
      deleteDialog.value = true
    }

    const executeDelete = () => {
      isDeleting.value = true
      emit('delete', {
        id: props.customer.id,
        callback: () => {
          isDeleting.value = false
          deleteDialog.value = false
        },
      })
    }

    watch(() => props.customer, () => {
      if (!isEditing.value) {
        initializeForm()
      }
    }, { deep: true })

    // 年を登録するチェックボックスが変更されたら誕生日を変換
    watch(() => includeYear.value, newVal => {
      if (!formData.value.birthday) return

      const date = new Date(formData.value.birthday)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      if (newVal) {
        // 年ありに変更 - 1900年なら今年に変換
        if (date.getFullYear() === 1900) {
          const currentYear = new Date().getFullYear()
          formData.value.birthday = `${currentYear}-${month}-${day}`
        }
      } else {
        // 年なしに変更 - 1900年に変換
        formData.value.birthday = `1900-${month}-${day}`
      }
    })

    return {
      form,
      formValid,
      isEditing,
      isSaving,
      isDeleting,
      deleteDialog,
      formData,
      includeYear,
      customFields,
      customFieldsWithValues,
      formatCustomFieldValue,
      customerMemberNumber,
      customerName,
      customerBirthday,
      customerReceiptAddressee,
      hasReceiptAddressee,
      copyReceiptAddressee,
      customerVisitCount,
      customerLastVisitedAt,
      customerCreatedAt,
      birthdayDisplay,
      birthdayPickerValue,
      onBirthdaySelect,
      clearBirthday,
      startEdit,
      cancelEdit,
      saveEdit,
      confirmDelete,
      executeDelete,
      icons: {
        mdiPencil,
        mdiContentCopy,
      },
    }
  },
}
</script>
